import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { FileText, Loader2, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format, differenceInDays } from 'date-fns';

interface DataRightsSectionProps {
  userId: string;
  userType: 'client' | 'practitioner';
  userEmail: string;
}

interface DsarRequest {
  id: string;
  request_type: string;
  status: string;
  message: string | null;
  submitted_at: string;
  deadline_at: string | null;
  completed_at: string | null;
  rejection_reason: string | null;
}

const REQUEST_TYPES = ['access', 'deletion', 'rectification', 'portability'] as const;

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
};

const DataRightsSection: React.FC<DataRightsSectionProps> = ({ userId, userType, userEmail }) => {
  const { t } = useTranslation('settings');
  const { toast } = useToast();
  const [requestType, setRequestType] = useState<string>('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState<DsarRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRequests = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('dsar_requests' as any)
      .select('id, request_type, status, message, submitted_at, deadline_at, completed_at, rejection_reason')
      .eq('requester_id', userId)
      .order('submitted_at', { ascending: false });

    if (!error && data) {
      setRequests(data as unknown as DsarRequest[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRequests();
  }, [userId]);

  const handleSubmit = async () => {
    if (!requestType) return;
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('dsar_requests' as any)
        .insert({
          requester_id: userId,
          requester_type: userType,
          requester_email: userEmail,
          request_type: requestType,
          message: message || null,
        } as any);

      if (error) throw error;

      toast({
        title: t('dataRights.submitted', { defaultValue: 'Request Submitted' }),
        description: t('dataRights.submittedDesc', { defaultValue: 'Your data rights request has been submitted. We will respond within 30 days.' }),
      });
      setRequestType('');
      setMessage('');
      fetchRequests();
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: t('dataRights.submitFailed', { defaultValue: 'Submission Failed' }),
        description: err.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getRequestTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      access: t('dataRights.typeAccess', { defaultValue: 'Access My Data' }),
      deletion: t('dataRights.typeDeletion', { defaultValue: 'Delete My Data' }),
      rectification: t('dataRights.typeRectification', { defaultValue: 'Correct My Data' }),
      portability: t('dataRights.typePortability', { defaultValue: 'Export My Data' }),
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: t('dataRights.statusPending', { defaultValue: 'Pending' }),
      in_progress: t('dataRights.statusInProgress', { defaultValue: 'In Progress' }),
      completed: t('dataRights.statusCompleted', { defaultValue: 'Completed' }),
      rejected: t('dataRights.statusRejected', { defaultValue: 'Rejected' }),
    };
    return labels[status] || status;
  };

  const getDeadlineBadge = (deadlineStr: string | null) => {
    if (!deadlineStr) return null;
    const daysLeft = differenceInDays(new Date(deadlineStr), new Date());
    if (daysLeft < 0) return <Badge variant="destructive">{t('dataRights.overdue', { defaultValue: 'Overdue' })}</Badge>;
    if (daysLeft <= 7) return <Badge variant="destructive">{daysLeft}d left</Badge>;
    if (daysLeft <= 15) return <Badge variant="secondary">{daysLeft}d left</Badge>;
    return <Badge variant="outline">{daysLeft}d left</Badge>;
  };

  return (
    <Card className="mt-4">
      <CardHeader className="pb-3 sm:pb-6">
        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
          <FileText className="w-5 h-5" />
          {t('dataRights.title', { defaultValue: 'Data Rights Request' })}
        </CardTitle>
        <CardDescription className="text-sm">
          {t('dataRights.description', { defaultValue: 'Submit a request to access, delete, correct, or export your personal data (GDPR/CCPA).' })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Request Form */}
        <div className="space-y-3 border rounded-lg p-4">
          <div className="space-y-2">
            <Label>{t('dataRights.requestType', { defaultValue: 'Request Type' })}</Label>
            <Select value={requestType} onValueChange={setRequestType}>
              <SelectTrigger>
                <SelectValue placeholder={t('dataRights.selectType', { defaultValue: 'Select request type...' })} />
              </SelectTrigger>
              <SelectContent>
                {REQUEST_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {getRequestTypeLabel(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('dataRights.message', { defaultValue: 'Message (optional)' })}</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('dataRights.messagePlaceholder', { defaultValue: 'Provide additional details about your request...' })}
              rows={3}
            />
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={!requestType || submitting} className="gap-2">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {t('dataRights.submit', { defaultValue: 'Submit Request' })}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('dataRights.confirmTitle', { defaultValue: 'Submit Data Rights Request?' })}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('dataRights.confirmDesc', { defaultValue: 'Your request will be reviewed by our team. We are legally required to respond within 30 days.' })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('dataPrivacy.cancel', { defaultValue: 'Cancel' })}</AlertDialogCancel>
                <AlertDialogAction onClick={handleSubmit}>
                  {t('dataRights.confirmSubmit', { defaultValue: 'Yes, Submit' })}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* Request History */}
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : requests.length > 0 ? (
          <div className="space-y-2">
            <h4 className="font-medium text-sm">{t('dataRights.history', { defaultValue: 'Your Requests' })}</h4>
            <div className="space-y-2">
              {requests.map((req) => (
                <div key={req.id} className="border rounded-lg p-3 space-y-1">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{getRequestTypeLabel(req.request_type)}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[req.status] || ''}`}>
                        {getStatusLabel(req.status)}
                      </span>
                    </div>
                    {req.status !== 'completed' && req.status !== 'rejected' && getDeadlineBadge(req.deadline_at)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t('dataRights.submittedOn', { defaultValue: 'Submitted' })}: {format(new Date(req.submitted_at), 'MMM d, yyyy')}
                    {req.completed_at && ` · ${t('dataRights.completedOn', { defaultValue: 'Completed' })}: ${format(new Date(req.completed_at), 'MMM d, yyyy')}`}
                  </div>
                  {req.message && (
                    <p className="text-xs text-muted-foreground mt-1">{req.message}</p>
                  )}
                  {req.rejection_reason && (
                    <p className="text-xs text-destructive mt-1">
                      {t('dataRights.rejectionReason', { defaultValue: 'Reason' })}: {req.rejection_reason}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default DataRightsSection;
